import { useState } from 'react';

import { Button, Dialog, DialogContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { ConnectEmailDialog } from '@modules/_shared/components/email-connect/ConnectEmailDialog';
import type { EmailEditorSnippetData } from '@modules/_shared/components/email-editor';
import { EmailForm } from '@modules/_shared/components/email-form/EmailForm';
import type {
  EmailFallbackRecipient,
  EmailFormRecipient,
  EmailTemplateSuggestion,
} from '@modules/_shared/components/email-form/types';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useEmailConnectedRefresh } from '@modules/_shared/hooks/useEmailConnectedRefresh';
import { useHasValidNylasAccount } from '@modules/_shared/hooks/useHasValidNylasAccount';
import { useLocale } from '@shared/hooks/locale';

type Props = {
  open: boolean;
  onClose: () => void;
  initialRecipients: EmailFormRecipient[];
  fallbackToRecipients?: EmailFallbackRecipient[];
  templateSuggestions?: EmailTemplateSuggestion[];
  parentGlobalId?: string | null;
  parentGlobalIds?: string[];
  initialSubject?: string;
  initialBodyValue?: React.ComponentProps<typeof EmailForm>['initialBodyValue'];
  initialTemplate?: React.ComponentProps<typeof EmailForm>['initialTemplate'];
  campaignMode: boolean;
  // Reply / forward prefill (recipients, subject, quote, threading id).
  mode?: React.ComponentProps<typeof EmailForm>['mode'];
  replyToMessage?: React.ComponentProps<typeof EmailForm>['replyToMessage'];
  forwardMessage?: React.ComponentProps<typeof EmailForm>['forwardMessage'];
  onEmailSent?: React.ComponentProps<typeof EmailForm>['onEmailSent'];
  draftKey?: React.ComponentProps<typeof EmailForm>['draftKey'];
  inheritsFiling?: React.ComponentProps<typeof EmailForm>['inheritsFiling'];
  filingReady?: React.ComponentProps<typeof EmailForm>['filingReady'];
  // Fires once the "Mass mail sent" snackbar has fully closed (auto-hide or
  // manual dismiss). Callers that keep this dialog mounted after a send only
  // for the snackbar's sake can run their cleanup (e.g. clearing a bulk
  // selection) here instead of in `onClose`.
  onCampaignSentDismissed?: () => void;
  snippetLotId?: string | null;
  snippetDevelopmentId?: string | null;
  presetSnippet?: EmailEditorSnippetData | null;
};

export const EmailFormDialog = ({
  open,
  onClose,
  initialRecipients,
  fallbackToRecipients,
  templateSuggestions,
  parentGlobalId,
  parentGlobalIds,
  initialSubject,
  initialBodyValue,
  initialTemplate,
  campaignMode,
  mode,
  replyToMessage,
  forwardMessage,
  onEmailSent,
  draftKey,
  inheritsFiling,
  filingReady,
  onCampaignSentDismissed,
  snippetLotId,
  snippetDevelopmentId,
  presetSnippet,
}: Props) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const hasValidNylasAccount = useHasValidNylasAccount();
  const onEmailConnected = useEmailConnectedRefresh();
  // Campaign sends confirm via a snackbar hosted here (the form unmounts with
  // the dialog) — the user stays on the page, with a link to the campaigns list.
  const [campaignSentOpen, setCampaignSentOpen] = useState(false);

  return (
    <>
      {hasValidNylasAccount ? (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
          <DialogContent sx={{ p: 0 }}>
            <EmailForm
              onClose={onClose}
              parentGlobalId={parentGlobalId}
              parentGlobalIds={parentGlobalIds}
              initialRecipients={initialRecipients}
              fallbackToRecipients={fallbackToRecipients}
              templateSuggestions={templateSuggestions}
              initialSubject={initialSubject}
              initialBodyValue={initialBodyValue}
              initialTemplate={initialTemplate}
              campaignMode={campaignMode}
              mode={mode}
              replyToMessage={replyToMessage}
              forwardMessage={forwardMessage}
              onEmailSent={onEmailSent}
              draftKey={draftKey}
              inheritsFiling={inheritsFiling}
              filingReady={filingReady}
              onCampaignSent={() => setCampaignSentOpen(true)}
              snippetLotId={snippetLotId}
              snippetDevelopmentId={snippetDevelopmentId}
              presetSnippet={presetSnippet}
              surface="dialog"
            />
          </DialogContent>
        </Dialog>
      ) : (
        <ConnectEmailDialog
          open={open}
          onClose={onClose}
          onSuccess={onEmailConnected}
          onError={onClose}
        />
      )}
      {campaignSentOpen && (
        <RASnackbar
          severity="success"
          message={t('massMailSent')}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/emails/campaigns')}
            >
              {t('View')}
            </Button>
          }
          onClose={() => {
            setCampaignSentOpen(false);
            onCampaignSentDismissed?.();
          }}
        />
      )}
    </>
  );
};
